'use client';
import { isoDate } from '@mnd-frontend/dates';
import { useState } from 'react';
import styled from 'styled-components';
import type { Report2VisibilityBenchmarkShareOfVoiceFragment } from '../../../../generated/types';
import { InteractionContextProvider } from '../../contexts/InteractionContext';
import {
  useReportDynamicData,
  useReportStaticData,
  useReportTracking,
  useReportTranslation,
} from '../../hooks';
import { getGraphColors } from '../../utils';
import { HitsModal, type HitsModalDetails, SkeletonBarChart } from '../common';
import { DonutChart, DonutContainer } from './DonutChart';
import { LegendTableShareOfVoice } from './GraphLegend/Legend';

const Wrapper = styled.div`
  position: relative;
`;
export const ShareOfMentionsGraph = ({
  competitorMetrics,
  reportMetrics,
  loading,
}: {
  competitorMetrics: Report2VisibilityBenchmarkShareOfVoiceFragment['competitorMetrics'];
  reportMetrics: Report2VisibilityBenchmarkShareOfVoiceFragment['reportMetrics'];
  loading: boolean;
}) => {
  const { t } = useReportTranslation();
  const [details, setDetails] = useState<{
    searchProfileIds: string[] | null;
    details: HitsModalDetails;
  } | null>(null);
  const onModalClose = () => setDetails(null);
  const { brand, toDate, fromDate } = useReportStaticData();
  const { brandColor } = useReportDynamicData();

  const sorted = [...competitorMetrics].sort((a, b) => {
    return a.mentions < b.mentions ? 1 : -1;
  });

  const colors = getGraphColors(brandColor, sorted.length);

  const competitorData = sorted.map((m, index) => ({
    id: m.searchProfileId,
    name: m.brandName,
    label: m.brandName,
    value: m.mentions,
    share: m.share,
    trend: m.trendPercentage,
    hitIds: m.hitIds || [],
    searchProfileIds: [m.searchProfileId],
    highlighted: false,
    groupId: '',
    color: colors[index],
    index,
  }));

  const brandData = {
    id: brand,
    name: brand,
    label: brand,
    groupId: brand,
    value: reportMetrics.mentions,
    share: reportMetrics.share,
    trend: reportMetrics.trendPercentage,
    hitIds: reportMetrics.hitIds || [],
    highlighted: true,
    color: brandColor,
    index: competitorData.length,
    searchProfileIds: null,
  };
  const data = [brandData, ...competitorData];

  const { track } = useReportTracking();

  const onArcClick = (id: string) => {
    const dataPoint = data.find(d => d.id === id);
    if (!dataPoint || dataPoint.hitIds.length === 0) return;
    track({
      event: 'Brand Reports: Clicked Share of voice graph',
    });
    setDetails({
      searchProfileIds: dataPoint.searchProfileIds,
      details: {
        startAt: isoDate(fromDate),
        endAt: isoDate(toDate),
        label: dataPoint.label,
      },
    });
  };

  return (
    <Wrapper data-testid="sentiment-overview-graph">
      {loading ? (
        <SkeletonBarChart />
      ) : (
        <>
          <DonutContainer>
            <InteractionContextProvider onClick={onArcClick}>
              <DonutChart data={data} unit={t('common_mentions')} />
              <LegendTableShareOfVoice data={data} />
            </InteractionContextProvider>
          </DonutContainer>

          {details && (
            <HitsModal
              onClose={onModalClose}
              details={details.details}
              searchProfileIds={details.searchProfileIds}
            />
          )}
        </>
      )}
    </Wrapper>
  );
};
